import { useEffect, useState } from "react";

import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import config from "@/environment";
import northbeamHeaderLogo from "@assets/northbeam-header-logo.svg";
import Bugsnag from "@bugsnag/js";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useInOnboarding } from "@hooks/use-in-onboarding";
import { useLazyRestApi } from "@hooks/use-lazy-rest-api";
import { HTTP_METHOD, useRestApi } from "@hooks/use-rest-api";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { Select } from "@shared/selects";

type FinalizeResponse = {
  status: string;
};

type BasicAccountInfo = {
  id: string;
  companyName: string;
};

type GetManagedAccountsResponse = {
  isAdmin: boolean;
  managedAccounts: BasicAccountInfo[];
};

const SUPPORTED_INTEGRATIONS = ["shopify"];

// TODO: Move to components.
export default function OAuthFinalizer() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const sig = searchParams.get("sig");
  const shop = searchParams.get("shop");
  const { integration } = useParams();
  const { user } = useUser();
  const [flowComplete, setFlowComplete] = useState<boolean>(false);
  const [shouldShowAccountPicker, setShouldShowAccountPicker] =
    useState<boolean>(false);
  const [selectedCompanyForSelect, setSelectedCompanyForSelect] = useState<
    string | null
  >(null);

  const { inOnboarding } = useInOnboarding();

  const navigate = useNavigate();

  const navbarColor = config.isDevelopmentEnvironment
    ? "navbar-light"
    : "navbar-dark";

  if (!integration || !SUPPORTED_INTEGRATIONS.includes(integration)) {
    throw new Error("Unsupported integration");
  }
  if (!sessionId || !sig) {
    throw new Error("Invalid session credentials");
  }

  const {
    loading: loadingExtraAccounts,
    error: extraAccountsError,
    data: extraAccountsData,
  } = useRestApi<GetManagedAccountsResponse>("/api/user/managed-accounts");
  const {
    loading: loadingFinalize,
    error: errorFinalize,
    data: dataFinalize,
    call: callFinalize,
  } = useLazyRestApi<FinalizeResponse>();

  useEffect(() => {
    if (!loadingExtraAccounts && !extraAccountsError && extraAccountsData) {
      if (extraAccountsData.managedAccounts.length === 0) {
        throw new Error("User does not have access to any accounts");
      }

      setShouldShowAccountPicker(true);
    }
  }, [
    loadingExtraAccounts,
    extraAccountsError,
    extraAccountsData,
    callFinalize,
    integration,
    sessionId,
    sig,
  ]);

  useEffect(() => {
    if (!loadingFinalize && !errorFinalize && dataFinalize) {
      setFlowComplete(true);
    }
  }, [loadingFinalize, errorFinalize, dataFinalize]);

  const hasMoreThanOneCompany =
    (extraAccountsData?.managedAccounts?.length ?? 0) > 1;

  const finalizeOauth = () => {
    callFinalize(`/api/callbacks/finalize-oauth?integration=${integration}`, {
      method: HTTP_METHOD.POST,
      data: {
        userId:
          selectedCompanyForSelect ??
          extraAccountsData?.managedAccounts?.[0]?.id,
        sessionId,
        sig,
      },
    });
  };

  const renderFinalizeRedirect = () => {
    if (!inOnboarding || user.defaultDisplayUserId) {
      return <Navigate to={`/${user.displayUserId}/settings/connections`} />;
    } else {
      return <Navigate to="/" />;
    }
  };

  const renderLoading = () => <LoadingSlide />;

  const handleError = () => {
    const error = extraAccountsError || errorFinalize || "Page State Error";
    Bugsnag.notify(`Unable to complete OAuth flow: ${error.toString()}`);
    throw error;
  };

  const getCompanyDataForSelect = () =>
    extraAccountsData?.managedAccounts.map((account) => ({
      label: account.companyName,
      value: account.id,
    })) ?? [];

  if (extraAccountsError || errorFinalize) {
    handleError();
    return null;
  }

  if (loadingExtraAccounts || loadingFinalize) {
    return renderLoading();
  }

  if (flowComplete) {
    return renderFinalizeRedirect();
  }

  return (
    <>
      <div
        className={`col p-0 h-100 color-white flex flex-column justify-content-between vertical-navbar ${navbarColor} z-navbar`}
      />
      <div
        className={`w-100 horizontal-navbar position-fixed flex justify-content-between ${navbarColor} z-navbar`}
      >
        <img
          src={northbeamHeaderLogo}
          alt="Northbeam header"
          className="northbeam-header-logo"
        />
      </div>
      <Modal
        isOpen={shouldShowAccountPicker}
        onClose={() => navigate("/")}
        hideCloseButton
      >
        <div className="flex flex-col">
          <p className="font-bold text-lg mb-4">Connect your shopify store</p>
          <p>
            <b>Shopify store:</b> {shop}
          </p>
          <p>
            If this is not the shopify store you intended to connect, cancel and
            login to the appropriate store before trying again.
          </p>
          {hasMoreThanOneCompany && (
            <Select
              isDisabled={false}
              isClearable={false}
              isSearchable={true}
              value={selectedCompanyForSelect}
              options={getCompanyDataForSelect()}
              onChange={setSelectedCompanyForSelect}
              width="100%"
              placeholder="Select the company you want to connect your store to..."
            />
          )}
          <div className="self-end">
            <PrimaryButton variant="danger" onClick={() => navigate("/")}>
              CANCEL
            </PrimaryButton>
            <PrimaryButton
              className="mt-4 mr-2"
              onClick={finalizeOauth}
              isDisabled={hasMoreThanOneCompany && !selectedCompanyForSelect}
            >
              CONNECT
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </>
  );
}
